import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  Calendar,
  Search,
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Settings,
  ClipboardList,
  Clock,
  Copy,
  Trash2,
  Wand2,
} from 'lucide-react'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { useSessionStore } from '../../stores/useSessionStore'
import { wizardHref } from './setup/eventSetupStatus'
import { SimpleEvent } from '../../model/Event'
import { isEventPast } from '../../lib/eventDates'
import { DateTime } from 'luxon'
import { toast } from 'sonner'
import { AddEventDialog } from './AddEventDialog'
import { DuplicateEventDialog } from './DuplicateEventDialog'

const formatDate = (date: Date | string | number | undefined) => {
  if (!date) return '-'
  try {
    let d: DateTime
    if (typeof date === 'number') d = DateTime.fromMillis(date)
    else if (typeof date === 'string') d = DateTime.fromISO(date)
    else d = DateTime.fromJSDate(date)
    return d.isValid ? d.toFormat('MMM dd, yyyy') : '-'
  } catch {
    return '-'
  }
}

export const EventList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showPastEvents, setShowPastEvents] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [eventToDuplicate, setEventToDuplicate] = useState<SimpleEvent | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<SimpleEvent | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  const navigate = useNavigate()
  const { events, refreshEvents, discardEvent, isLoading } = useApplicationStore()
  const { hasAdmin, canCreateEventsForAnyone, hasScheduleAdminFor } =
    useSessionStore()
  const canCreateEvents = canCreateEventsForAnyone()

  const deleteConfirmed =
    !!eventToDelete && deleteConfirmText.trim() === eventToDelete.name

  const handleDelete = async () => {
    if (!eventToDelete || !deleteConfirmed) return
    setDeleting(true)
    try {
      await discardEvent(eventToDelete)
      toast.success('Event deleted')
      setDeleteDialogOpen(false)
      setEventToDelete(null)
      setDeleteConfirmText('')
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete event'
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    refreshEvents()
  }, [refreshEvents])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, showPastEvents])

  const filteredAndSorted = events
    .filter((event) => {
      if (!showPastEvents && isEventPast(event)) return false
      if (!searchTerm) return true
      const term = searchTerm.toLowerCase()
      return (
        event.name.toLowerCase().includes(term) ||
        event.client?.name?.toLowerCase().includes(term)
      )
    })
    .sort((a, b) => {
      const aDate = new Date(a.startDate).getTime()
      const bDate = new Date(b.startDate).getTime()
      return sortOrder === 'desc' ? bDate - aDate : aDate - bDate
    })

  const totalItems = filteredAndSorted.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const paginatedEvents = filteredAndSorted.slice(startIndex, startIndex + pageSize)

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Events</h1>
              <p className="text-sm text-muted-foreground">
                {events.length} event{events.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refreshEvents()} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canCreateEvents && (
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Event
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search events or clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Checkbox
                id="show-past-events"
                checked={showPastEvents}
                onCheckedChange={(c) => setShowPastEvents(c === true)}
              />
              <label
                htmlFor="show-past-events"
                className="text-sm font-medium text-muted-foreground cursor-pointer"
              >
                Show past events
              </label>
            </div>
            <Select value={sortOrder} onValueChange={(v: any) => setSortOrder(v)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest First</SelectItem>
                <SelectItem value="asc">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading() ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Loading events...</p>
            </div>
          ) : paginatedEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Calendar className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-4 text-sm font-medium">No events found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Get started by creating your first event'}
              </p>
              {!searchTerm && canCreateEvents && (
                <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Event
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[30%]">Event</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEvents.map((event) => {
                    const past = isEventPast(event)
                    return (
                      <TableRow
                        key={event.id}
                        className={`transition-colors ${past ? 'opacity-60' : ''}`}
                      >
                        <TableCell>
                          <Link
                            to={`/event/${event.id}/config`}
                            className="flex items-center gap-3"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                              {event.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-primary hover:underline">
                              {event.name}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          {event.client ? (
                            <Badge variant="secondary" className="text-xs">
                              {event.client.name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <div className="text-sm">
                            {formatDate(event.startDate)} - {formatDate(event.endDate)}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {past ? (
                            <Badge variant="secondary" className="text-xs text-muted-foreground">
                              <Clock className="h-3 w-3 mr-1" />
                              Past
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 hover:bg-green-50">
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {(hasAdmin() ||
                                (event.client &&
                                  hasScheduleAdminFor(event.client.id))) && (
                                <DropdownMenuItem
                                  onClick={() => navigate(wizardHref(event.id))}
                                >
                                  <Wand2 className="mr-2 h-4 w-4" />
                                  Schedule Setup Wizard
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => navigate(`/event/${event.id}/config`)}>
                                <Settings className="mr-2 h-4 w-4" />
                                Configure
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/event/${event.id}/provision`)}>
                                <ClipboardList className="mr-2 h-4 w-4" />
                                Provision
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/event/${event.id}/schedule`)}>
                                <Calendar className="mr-2 h-4 w-4" />
                                Schedule
                              </DropdownMenuItem>
                              {(hasAdmin() ||
                                (event.client &&
                                  hasScheduleAdminFor(event.client.id))) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEventToDuplicate(event)
                                      setDuplicateDialogOpen(true)
                                    }}
                                  >
                                    <Copy className="mr-2 h-4 w-4" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEventToDelete(event)
                                      setDeleteDialogOpen(true)
                                    }}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1) }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {startIndex + 1}-{Math.min(startIndex + pageSize, totalItems)} of {totalItems}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Event Dialog */}
      <AddEventDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />

      <DuplicateEventDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        event={eventToDuplicate}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteDialogOpen(false)
            setEventToDelete(null)
            setDeleteConfirmText('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. To confirm, type the event name{' '}
              <span className="font-semibold text-foreground">{eventToDelete?.name}</span>{' '}
              below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={eventToDelete?.name}
              disabled={deleting}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deleteConfirmed) handleDelete()
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting || !deleteConfirmed}>
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
